import React, {useState} from 'react';
import {observer} from 'mobx-react';
import {useIntl} from 'react-intl';
import Button from 'src/components/button';
import DataTable from 'src/components/dataTable';
import useAlerts from 'src/hooks/use-alerts';
import {ExemptionRow, useBlockExemptions} from './queries';
import {useRemoveBlockExemption} from './mutations';
import {ListState, RefreshButton, Truncated} from './list-state';
import ConfirmDialog from './confirm-dialog';
import ExemptionForm from './exemption-form';
import {errorMessageId} from './errors';
import {formatDateTime, orNotAvailable} from './format';
import styles from './billing-block.module.scss';

interface ExemptionsTabProps {
  readonly: boolean;
}

const noop = () => undefined;

const ExemptionsTab = ({readonly}: ExemptionsTabProps) => {
  const intl = useIntl();
  const {toast} = useAlerts();
  const query = useBlockExemptions();
  const remove = useRemoveBlockExemption();
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState<ExemptionRow | null>(null);
  const [removing, setRemoving] = useState<ExemptionRow | null>(null);

  const confirmRemove = () => {
    if (!removing) return;
    remove.mutate(removing.id, {
      onSuccess: () => toast.success(intl.formatMessage({id: 'billingBlockExemptionRemoved'})),
      onError: (error) => toast.error(intl.formatMessage({id: errorMessageId(error)})),
      onSettled: () => {
        setRemoving(null);
        setSelected(null);
      },
    });
  };

  const rows = query.rows.map((row) => ({
    ...row,
    targetLabel: orNotAvailable(intl, row.name),
    kindLabel: intl.formatMessage({id: row.kind === 'customer' ? 'billingBlockKindCustomer' : 'billingBlockKindDevice'}),
    noteLabel: row.note || '',
    createdAtLabel: formatDateTime(intl, row.createdAt),
  }));

  const actions = (
    <>
      <RefreshButton query={query} />
      {!readonly && (
        <>
          <Button className="neutral" color="secondary" size="sm" disabled={!selected} onClick={() => setRemoving(selected)}>
            {intl.formatMessage({id: 'billingBlockRemoveExemption'})}
          </Button>
          <Button className="solid" color="theme" size="sm" onClick={() => setCreating(true)}>
            {intl.formatMessage({id: 'billingBlockNewExemption'})}
          </Button>
        </>
      )}
    </>
  );

  return (
    <section className={styles.section}>
      <ListState query={query} rows={rows} emptyMessage={intl.formatMessage({id: 'billingBlockEmptyExemptions'})} actions={actions}>
        {(list) => (
          <DataTable
            key={query.dataUpdatedAt}
            add={noop}
            edit={noop}
            remove={noop}
            showGroupButtons={false}
            keyField="id"
            list={list}
            onSelectRow={setSelected}
            extraHeaderEl={() => actions}
            headers={[
              {title: 'billingBlockColumnTarget', field: 'targetLabel', dataSort: true, dataFormat: (cell: string) => <Truncated text={cell} />},
              {title: 'billingBlockColumnKind', field: 'kindLabel', dataSort: true},
              {title: 'billingBlockColumnNote', field: 'noteLabel', dataFormat: (cell: string) => (cell ? <Truncated text={cell} /> : '')},
              {title: 'billingBlockColumnCreatedAt', field: 'createdAtLabel'},
            ]}
          />
        )}
      </ListState>
      {!readonly && <ExemptionForm open={creating} exemptions={query.rows} onClose={() => setCreating(false)} />}
      <ConfirmDialog
        open={!!removing}
        danger
        title={intl.formatMessage({id: 'billingBlockRemoveExemptionTitle'})}
        description={(
          <p>
            {intl.formatMessage({
              id: removing?.kind === 'customer' ? 'billingBlockRemoveExemptionBodyCustomer' : 'billingBlockRemoveExemptionBodyDevice',
            })}
          </p>
        )}
        confirmLabel={intl.formatMessage({id: 'billingBlockRemoveExemption'})}
        busy={remove.isLoading}
        onConfirm={confirmRemove}
        onCancel={() => setRemoving(null)}
      />
    </section>
  );
};

export default observer(ExemptionsTab);
